import {
  abs,
  dot,
  float,
  floor,
  If,
  max,
  min,
  perspectiveDepthToViewZ,
  vec2,
  vec3,
} from "three/tsl";

type Sampleable = {
  sample: (uv: unknown) => { r: unknown; rgb: { normalize: () => unknown } };
};

/**
 * Parent giStride hücresi: 9 tap (köşe, kenar ortası, merkez).
 * Sky tap yok sayılır. Çift kapı kırılırsa veya viewZ aralığı büyükse refine.
 */
export function parentCellNeedsRefine(args: {
  screenCoordinate: { x: unknown; y: unknown };
  fullResolution: { x: unknown; y: unknown };
  coarseStride: unknown;
  depth: Sampleable;
  normal: Sampleable;
  depthThreshold: unknown;
  normalThreshold: unknown;
  cameraNear: unknown;
  cameraFar: unknown;
  reversedDepth: unknown;
}): unknown {
  const s = float(args.coarseStride as never);
  const fullW = float(args.fullResolution.x as never);
  const fullH = float(args.fullResolution.y as never);
  const px = args.screenCoordinate.x as { div: (n: unknown) => unknown };
  const py = args.screenCoordinate.y as { div: (n: unknown) => unknown };
  const x0 = (
    floor(px.div(s) as never) as { mul: (n: unknown) => unknown }
  ).mul(s);
  const y0 = (
    floor(py.div(s) as never) as { mul: (n: unknown) => unknown }
  ).mul(s);
  const x1 = min(
    (x0 as { add: (n: unknown) => unknown }).add(s) as never,
    fullW.sub(1) as never,
  );
  const y1 = min(
    (y0 as { add: (n: unknown) => unknown }).add(s) as never,
    fullH.sub(1) as never,
  );
  const mx = (
    (x0 as { add: (v: unknown) => { mul: (n: number) => unknown } }).add(
      x1,
    ) as { mul: (n: number) => unknown }
  ).mul(0.5);
  const my = (
    (y0 as { add: (v: unknown) => { mul: (n: number) => unknown } }).add(
      y1,
    ) as { mul: (n: number) => unknown }
  ).mul(0.5);
  const taps: [unknown, unknown][] = [
    [x0, y0],
    [x1, y0],
    [x0, y1],
    [x1, y1],
    [mx, y0],
    [mx, y1],
    [x0, my],
    [x1, my],
    [mx, my],
  ];

  const near = float(args.cameraNear as never);
  const far = float(args.cameraFar as never);
  const dThresh = float(args.depthThreshold as never);
  const nThresh = float(args.normalThreshold as never);
  const reversed = float(args.reversedDepth as never);

  const zs: unknown[] = [];
  const ns: unknown[] = [];
  const oks: unknown[] = [];

  for (const [tx, ty] of taps) {
    const tapUv = vec2(
      (tx as { add: (n: number) => { div: (d: unknown) => unknown } })
        .add(0.5)
        .div(fullW) as never,
      (ty as { add: (n: number) => { div: (d: unknown) => unknown } })
        .add(0.5)
        .div(fullH) as never,
    );
    const raw = args.depth.sample(tapUv).r;
    const sky = (
      reversed as {
        greaterThan: (n: number) => {
          select: (a: unknown, b: unknown) => unknown;
        };
      }
    )
      .greaterThan(0.5)
      .select(
        (raw as { lessThanEqual: (n: number) => unknown }).lessThanEqual(1e-5),
        (raw as { greaterThanEqual: (n: number) => unknown }).greaterThanEqual(
          0.999,
        ),
      );
    const viewZ = perspectiveDepthToViewZ(raw as never, near, far);
    const nrm = args.normal.sample(tapUv).rgb.normalize();
    const zVar = float(0).toVar();
    const nVar = vec3(0).toVar();
    const okVar = float(0).toVar();
    If((sky as { not: () => unknown }).not() as never, () => {
      (zVar as { assign: (v: unknown) => void }).assign(viewZ);
      (nVar as { assign: (v: unknown) => void }).assign(nrm);
      (okVar as { assign: (v: unknown) => void }).assign(float(1));
    });
    zs.push(zVar);
    ns.push(nVar);
    oks.push(okVar);
  }

  const minZ = float(1e10).toVar();
  const maxZ = float(-1e10).toVar();
  const validCount = float(0).toVar();
  const bad = float(0).toVar();

  for (let i = 0; i < oks.length; i++) {
    If(
      (oks[i] as { greaterThan: (n: number) => unknown }).greaterThan(
        0.5,
      ) as never,
      () => {
        (validCount as { addAssign: (n: unknown) => void }).addAssign(float(1));
        (minZ as { assign: (v: unknown) => void }).assign(
          min(minZ as never, zs[i] as never),
        );
        (maxZ as { assign: (v: unknown) => void }).assign(
          max(maxZ as never, zs[i] as never),
        );
      },
    );
  }

  If(
    (maxZ as { sub: (v: unknown) => { greaterThan: (n: unknown) => unknown } })
      .sub(minZ)
      .greaterThan(dThresh) as never,
    () => {
      (bad as { assign: (v: unknown) => void }).assign(float(1));
    },
  );

  for (let i = 0; i < oks.length; i++) {
    for (let j = i + 1; j < oks.length; j++) {
      const both = (
        oks[i] as {
          greaterThan: (n: number) => { and: (o: unknown) => unknown };
        }
      )
        .greaterThan(0.5)
        .and(
          (oks[j] as { greaterThan: (n: number) => unknown }).greaterThan(0.5),
        );
      const depthBad = abs(
        (zs[i] as { sub: (v: unknown) => unknown }).sub(zs[j] as never) as never,
      ).greaterThanEqual(dThresh as never);
      const normalBad = (
        dot(ns[i] as never, ns[j] as never) as {
          lessThan: (n: unknown) => unknown;
        }
      ).lessThan(nThresh);
      If(
        (both as { and: (o: unknown) => unknown }).and(
          (depthBad as { or: (o: unknown) => unknown }).or(normalBad),
        ) as never,
        () => {
          (bad as { assign: (v: unknown) => void }).assign(float(1));
        },
      );
    }
  }

  return (
    validCount as {
      greaterThan: (n: number) => { and: (o: unknown) => unknown };
    }
  )
    .greaterThan(1.5)
    .and((bad as { greaterThan: (n: number) => unknown }).greaterThan(0.5));
}
